import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/ui-components/ui/button";
import { Dialog, DialogContent } from "@/ui-components/ui/dialog";
import { Rocket, Database, LayoutTemplate, Share2 } from "lucide-react";
import { type PlanType } from "./shared";

interface OnboardingCompleteDialogProps {
  open?: boolean;
  plan: PlanType;
  onComplete: () => void;
  onCloseSettingsHint?: () => void;
}

export const OnboardingCompleteDialog = ({
  open = true,
  plan,
  onComplete,
  onCloseSettingsHint,
}: OnboardingCompleteDialogProps) => {
  return (
    <Dialog open={open} onOpenChange={() => undefined}>
      <DialogContent
        isClosable={false}
        className="flex max-h-[90vh] w-[calc(100%-2rem)] flex-col overflow-hidden rounded-3xl sm:rounded-3xl border border-gray-100 p-0 shadow-xl sm:w-full sm:max-w-md bg-white"
        onEscapeKeyDown={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
      >
        <div className="flex flex-col items-center p-8 text-center pb-6">
          <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
            <Rocket className="h-7 w-7" />
          </div>
          
          <h2 className="mb-3 text-2xl font-bold tracking-tight text-gray-900">Fast geschafft!</h2>
          <p className="text-sm leading-relaxed text-gray-500">
            Damit die Plattform für deinen Verein richtig nützlich wird, hier die nächsten Schritte.
          </p>
        </div>

        <div className="px-8 pb-8 space-y-8 overflow-y-auto">
          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-50 border border-gray-100 text-gray-600">
                <Database className="h-4 w-4" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Stammdaten anlegen</h3>
                <p className="mt-1 text-sm leading-relaxed text-gray-500">Hinterlege Mannschaften, Spieler und Sponsoren als Basis für deine Posts.</p>
              </div>
            </div>
            
            <div className="flex items-start gap-4">
              <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-50 border border-gray-100 text-gray-600">
                <LayoutTemplate className="h-4 w-4" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Templates finden</h3>
                <p className="mt-1 text-sm leading-relaxed text-gray-500">Importiere aus dem Store Templates, die zum Style deines Vereins passen.</p>
              </div>
            </div>
            
            <div className="flex items-start gap-4">
              <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-50 border border-gray-100 text-gray-600">
                <Share2 className="h-4 w-4" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Social Media verbinden</h3>
                <p className="mt-1 text-sm leading-relaxed text-gray-500">Verknüpfe deine Kanäle, um direkt aus SportyPost zu posten.</p>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-4 pt-2">
            <Button
              onClick={onComplete}
              className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all"
            >
              Alles klar, los geht&apos;s!
            </Button>
            <p className="text-center text-xs text-gray-400">
              Du findest die Tour jederzeit wieder in deinen{" "}
              <Link to="/settings" onClick={onCloseSettingsHint} className="font-medium text-gray-600 hover:text-gray-900 underline underline-offset-2">
                Einstellungen
              </Link>.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export type { OnboardingCompleteDialogProps };
